import React, { useState } from 'react';
import { BaseFormField } from './BaseFormField';

type InputType = 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search';

interface TextInputProps {
    name: string;
    label: string;
    type?: InputType;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    maxLength?: number;
    minLength?: number;
    pattern?: RegExp;
    onValueChange?: (value: string, values: any) => void;
    validateName?: boolean;
    getCombinedNameError?: (values: any) => void;
    autoComplete?: string;
    inputMode?: 'none' | 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url';
    step?: number;
    min?: number;
    max?: number;
}

export const TextInput: React.FC<TextInputProps> = ({
    name,
    label,
    type = 'text',
    placeholder = '',
    required = false,
    disabled = false,
    maxLength = 100,
    minLength,
    pattern = /^[a-zA-Z0-9 ]*$/,
    onValueChange,
    validateName = false,
    getCombinedNameError,
    autoComplete,
    inputMode,
    step,
    min,
    max,
}) => {
    const [showPass, setShowPass] = useState(false);

    const getValidationPattern = (inputType: InputType): RegExp => {
        switch (inputType) {
            case 'email':
                return /^[a-zA-Z0-9._%+-@]*$/;
            case 'number':
            case 'tel':
                return /^\d*$/;
            case 'password':
                return /^[\S]*$/;
            case 'url':
                return /^[a-zA-Z0-9./:?#[\]@!$&'()*+,;=%-]*$/;
            default:
                return pattern;
        }
    };

    const validationPattern = getValidationPattern(type);

    return (
        <div className="mb-4">
            <label className="fs-14 fw-400 mb-2" htmlFor={name}>
                {label} {required && <span className="lt-text-error">*</span>}
            </label>
            <BaseFormField name={name}>
                {({ field, error, values, setFieldValue, setFieldTouched }) => {
                    if (validateName && getCombinedNameError && field.value?.length > 0) {
                        getCombinedNameError(values);
                    }

                    const handleChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
                        const value = ev.target.value;

                        const skipPatternValidation = ['password', 'email'].includes(type);

                        if (skipPatternValidation || validationPattern.test(value) || value === '') {
                            setFieldTouched(field.name);
                            setFieldValue(field.name, value);
                            onValueChange?.(value, { ...values, [field.name]: value });
                        }
                    };

                    return (
                        <>
                            <div className="position-relative">
                                <input
                                    type={type === 'password' ? (showPass ? 'text' : 'password') : type}
                                    value={field.value || ''}
                                    disabled={disabled}
                                    onChange={handleChange}
                                    className={`form-control`}
                                    placeholder={placeholder}
                                    maxLength={maxLength}
                                    minLength={minLength}
                                    id={name}
                                    autoComplete={autoComplete}
                                    inputMode={inputMode}
                                    step={step}
                                    min={min}
                                    max={max}
                                />
                                {type === 'password' && (
                                    <i
                                        onClick={() => setShowPass(!showPass)}
                                        className={`bi  ${
                                            showPass ? 'bi-eye' : 'bi-eye-slash'
                                        } password-toggle cursor-pointer`}
                                        style={{
                                            position: 'absolute',
                                            right: '12px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                        }}
                                    />
                                )}
                            </div>
                            {typeof error === 'string' && <small className="text-danger fs-12">{error}</small>}
                        </>
                    );
                }}
            </BaseFormField>
        </div>
    );
};
